<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import MagicWandIcon from '@/assets/icons/magic-wand.svg';
import MagicFeatherIcon from '@/assets/icons/magic-feather.svg';
import CrossIcon from '@/assets/icons/cross2.svg';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg';
import ParagraphBigIcon from '@/assets/icons/paragragh-big.svg';
import ParagraphSmallIcon from '@/assets/icons/paragragh-small.svg';
import AbcWaveIcon from '@/assets/icons/abc-wave.svg';
import MagicWandConversationMessage from './MagicWandConversationMessage.vue';
import MagicWandMessageLoading from './MagicWandMessageLoading.vue';
import MagicWandMessageError from './MagicWandMessageError.vue';
import { MESSAGE_ROLES } from './MessageRoles';

const POPUP_HEIGHT = 400;

const store = useStore();

const { initialText = '', promptContext = '' } = defineProps({
  initialText: String,
  promptContext: String,
});

const emit = defineEmits(['insert']);

const popupOpen = ref(false);
const error = ref(false);
const loading = ref(false);
const conversationId = ref(crypto.randomUUID());
const conversationHistory = ref([]);

const containerRef = useTemplateRef('containerRef');
const messagesContainerRef = useTemplateRef('messagesContainerRef');

const user = computed(() => store.getters['user']);

let popupPosition = computed(() => {
  popupOpen.value; // retrigger computed when popup opens or closes.

  const anchor = containerRef.value;
  if (!anchor) return { left: 0 + 'px', top: 0 + 'px' };

  let { right, top } = anchor.getBoundingClientRect();

  if (top + POPUP_HEIGHT >= window.innerHeight) {
    top = window.innerHeight - POPUP_HEIGHT - 32;
  }

  return { left: right + 8 + 'px', top: top + 'px' };
});

const arePremiumFeaturesAvailable = computed(() => user.value.organization?.premiumFeaturesAvailable);

watch(
  () => initialText,
  () => {
    popupOpen.value = false;
  }
);

watch(popupOpen, newValue => {
  if (!newValue) return;

  error.value = false;
  loading.value = false;

  conversationId.value = crypto.randomUUID();

  conversationHistory.value = [
    {
      id: -1,
      role: MESSAGE_ROLES.System,
      text:
        initialText.trim().length === 0
          ? 'Quel type de contenu souhaitez vous générer ?'
          : 'Pour apporter des modifications à votre contenu, veuillez consulter les outils disponibles ci-dessous.',
    },
  ];
});

watch(
  conversationHistory,
  async () => {
    await nextTick();

    if (messagesContainerRef.value) {
      messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
    }
  },
  { deep: true }
);

const sendMessage = (userInput, initialText = '', userInputForHistoric = '') => {
  if (loading.value) return;
  loading.value = true;

  let context = '';
  if (conversationHistory.value.length === 1) {
    context =
      "Voici le contexte sur lequel je souhaite que vous travailliez EN GARDANT LA STRUCTURE HTML DU TEXTE D'ORIGINE :\n" +
      promptContext +
      '\n\n';
  }

  let textToModify = '';
  if (initialText.trim().length > 0) {
    textToModify = 'Voici le texte que je souhaite que vous modifiez :\n' + initialText + '\n\n';
  }

  conversationHistory.value.push({
    id: conversationHistory.value.length,
    role: MESSAGE_ROLES.User,
    text: userInputForHistoric ? userInputForHistoric : userInput,
  });

  store
    .dispatch('aiAssistant.sendMessage', {
      conversationId: conversationId.value,
      content: context + textToModify + userInput,
    })
    .then(response => {
      conversationHistory.value.push({
        id: conversationHistory.value.length,
        role: MESSAGE_ROLES.Assistant,
        text: response.content,
      });
    })
    .catch(() => {
      error.value = true;
    })
    .finally(() => {
      loading.value = false;
    });
};

const hoverText = ref('');
const hoverTextDiv = useTemplateRef('hover-text');
const handleHoverText = (event, msg) => {
  hoverText.value = msg;

  if (!msg) return;

  const { top, height, right } = event.target.getBoundingClientRect();
  hoverTextDiv.value.style.right = window.innerWidth - right + 'px';
  hoverTextDiv.value.style.top = top - height - 12 + 'px';
};
</script>

<template>
  <div v-if="arePremiumFeaturesAvailable" ref="containerRef">
    <MagicWandIcon class="icon" @click="popupOpen = !popupOpen" />

    <Transition name="popup">
      <div v-if="popupOpen" class="container" :style="{ ...popupPosition, height: POPUP_HEIGHT + 'px' }">
        <div class="header">
          <div><MagicFeatherIcon class="icon display" /> Baguette magique</div>

          <CrossIcon class="icon" @click="popupOpen = false" />
        </div>

        <div ref="messagesContainerRef" class="content">
          <MagicWandConversationMessage
            v-for="message in conversationHistory"
            :key="message.id"
            :message="message.text"
            :message-role="message.role"
            @insert="
              () => {
                emit('insert', message.text);
                popupOpen = false;
              }
            "
          />

          <MagicWandMessageError v-if="error" />

          <MagicWandMessageLoading v-else-if="loading" />
        </div>

        <div class="footer">
          <form
            class="input-container"
            autocomplete="off"
            @submit.prevent="
              e => {
                if (e.target.elements.prompt.value.trim().length === 0) return;

                sendMessage(e.target.elements.prompt.value, conversationHistory.length === 1 ? initialText : '');
                e.target.elements.prompt.value = '';
              }
            "
          >
            <input id="prompt" placeholder="Générer ou redéfinir du contenu" type="text" :disabled="error || loading" />

            <button :disabled="error || loading">
              <ArrowRightIcon class="icon" />
            </button>
          </form>

          <ParagraphBigIcon
            class="icon"
            :class="
              (error || loading || (initialText.trim().length === 0 && conversationHistory.length < 3)) && 'disabled'
            "
            @mouseenter="handleHoverText($event, 'Rallonger le texte')"
            @mouseleave="handleHoverText($event, '')"
            @click="sendMessage('Je veux rallonger le texte', conversationHistory.length === 1 ? initialText : '')"
          />

          <ParagraphSmallIcon
            class="icon"
            :class="
              (error || loading || (initialText.trim().length === 0 && conversationHistory.length < 3)) && 'disabled'
            "
            @mouseenter="handleHoverText($event, 'Raccourcir le texte')"
            @mouseleave="handleHoverText($event, '')"
            @click="sendMessage('Je veux raccourcir le texte', conversationHistory.length === 1 ? initialText : '')"
          />

          <AbcWaveIcon
            class="icon"
            :class="
              (error || loading || (initialText.trim().length === 0 && conversationHistory.length < 3)) && 'disabled'
            "
            @mouseenter="handleHoverText($event, 'Corriger les fautes et la grammaire du texte')"
            @mouseleave="handleHoverText($event, '')"
            @click="
              sendMessage(
                'Je veux corriger les fautes d\'orthographe et la grammaire du texte sans changer le contenu.',
                conversationHistory.length === 1 ? initialText : '',
                'Je veux corriger les fautes et la grammaire du texte'
              )
            "
          />

          <span v-show="hoverText" ref="hover-text" class="hover-text">{{ hoverText }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.icon {
  cursor: pointer;
  transition-duration: 100ms;
  width: 12px;
  height: 12px;

  &:hover {
    scale: 1.1;
  }

  &:active {
    scale: 0.9;
  }
}

.container {
  position: fixed;
  z-index: 1;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: -4px 4px 30px 0 #00000033;

  display: flex;
  flex-direction: column;
  color: colors.$darkest-grey-color;
  text-transform: none;
  box-sizing: border-box;
  width: 600px;

  .content {
    box-sizing: border-box;
    padding: 16px;
    background: #eee;

    display: flex;
    flex-direction: column;
    gap: 16px;
    justify-content: flex-start;
    align-items: flex-start;

    overflow-y: auto;
    flex-grow: 1;
  }

  .header,
  .footer {
    box-sizing: border-box;

    display: flex;
    justify-content: space-between;
    align-items: center;
    background: colors.$white-color;
    padding: 16px;
    border: 1px solid colors.$grey-background-color;
    flex-shrink: 0;
  }

  .header {
    border-radius: 4px 4px 0 0;

    .icon.display {
      cursor: default;
      scale: none;
    }

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  }

  .footer {
    position: relative;
    display: flex;
    gap: 8px;
    border-radius: 0 0 4px 4px;

    .input-container {
      flex-grow: 1;
      position: relative;
      display: flex;
      align-items: center;

      input {
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        border: 1px solid #333333;
        border-radius: 5px;
        padding: 8px 40px 8px 16px;

        &:disabled {
          pointer-events: none;
          cursor: not-allowed;
          color: colors.$grey-background-color;
          border: 1px solid colors.$grey-background-color;
        }
      }

      button {
        cursor: pointer;
        position: absolute;
        right: 0;
        display: flex;
        align-items: center;
        padding: 8px;
        border: none;
        background: none;

        &:disabled {
          pointer-events: none;
          cursor: not-allowed;

          &:before {
            border-left: 1px solid colors.$grey-background-color;
          }

          .icon {
            background: colors.$grey-background-color;
          }
        }

        &:before {
          position: absolute;
          content: '';
          display: block;
          height: 16px;
          border-left: 1px solid #333333;
          left: 0;
        }

        .icon {
          width: 8px;
          height: 8px;
          padding: 4px;
          background: #333333;
          border-radius: 999px;
          color: colors.$white-color;
        }
      }
    }

    > .icon {
      padding: 9px;
      border: 1px solid #333333;
      border-radius: 4px;
      width: 14px;
      height: 14px;

      &.disabled {
        pointer-events: none;
        cursor: not-allowed;
        scale: none;
        color: colors.$grey-background-color;
        border: 1px solid colors.$grey-background-color;
      }
    }

    .hover-text {
      position: fixed;
      border-radius: 5px;
      padding: 8px 16px;
      background-color: #333333;
      color: white;
      z-index: 1000;
      font-family: Mulish, sans-serif;
      font-weight: 300;
      font-size: 0.76rem;

      &:after {
        content: '';
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid #333333;
        position: absolute;
        bottom: -5px;
        right: 12px;
      }
    }
  }
}
</style>
